import type { TaskListResponse, TaskStatus } from '../services/api/types.gen';
import { parseISOToLocalDate } from './dateUtils';

/**
 * Documentation has entered the submit/QA/complete pipeline — mirrors
 * ``LUPA_SUBMITTED_VISIT_STATUSES`` in ``episode_lupa.py``. Excludes
 * not_started / saved drafts.
 */
export const LUPA_SUBMITTED_VISIT_STATUSES = new Set<TaskStatus>([
  'submitted_with_signature',
  'submitted',
  'pending_clinician_signature',
  'returned_for_review',
  'completed',
  'exported',
  'reopened',
]);

const isBillableForLupa = (task: TaskListResponse): boolean =>
  task.task_type === 'visit' &&
  task.billable_status === 'BILLABLE' &&
  !task.missed_visit_data;

/** Plot date used by LUPA scheduled windowing (scheduled_date), matching backend. */
export const getLupaPlotDate = (task: TaskListResponse): Date | undefined =>
  parseISOToLocalDate(task.scheduled_date);

/** Actual service date (`task_date` / visit_date) used for submitted guarantees. */
export const getLupaServiceDate = (task: TaskListResponse): Date | undefined =>
  parseISOToLocalDate(task.task_date ?? undefined);

export const isTaskInLupaPeriodWindow = (
  task: TaskListResponse,
  windowStart: Date,
  windowEnd: Date
): boolean => {
  const plotDate = getLupaPlotDate(task);
  if (!plotDate) {
    return false;
  }
  return plotDate >= windowStart && plotDate <= windowEnd;
};

const isServiceDateInLupaPeriodWindow = (
  task: TaskListResponse,
  windowStart: Date,
  windowEnd: Date
): boolean => {
  const serviceDate = getLupaServiceDate(task);
  if (!serviceDate) {
    return false;
  }
  return serviceDate >= windowStart && serviceDate <= windowEnd;
};

export type LupaVisitContributionKind = 'scheduled' | 'submitted';

/**
 * Billable visits contributing to a period's scheduled or submitted LUPA count.
 * Mirrors backend ``count_billable_visits`` / ``count_submitted_billable_visits``.
 *
 * Submitted additionally requires the service date in the same window, so a
 * visit scheduled on the last day of period 1 but completed in period 2 does
 * not inflate the "already delivered" figure for period 1.
 */
export const getLupaContributingVisits = (
  tasks: TaskListResponse[],
  windowStart: Date,
  windowEnd: Date,
  kind: LupaVisitContributionKind
): TaskListResponse[] =>
  tasks
    .filter(task => {
      if (!isBillableForLupa(task)) {
        return false;
      }
      if (!isTaskInLupaPeriodWindow(task, windowStart, windowEnd)) {
        return false;
      }
      if (kind === 'submitted') {
        return (
          LUPA_SUBMITTED_VISIT_STATUSES.has(task.status) &&
          isServiceDateInLupaPeriodWindow(task, windowStart, windowEnd)
        );
      }
      return true;
    })
    .sort((left, right) => {
      const leftDate = left.scheduled_date ?? '';
      const rightDate = right.scheduled_date ?? '';
      return leftDate.localeCompare(rightDate);
    });
